import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../lib/auth";

const BG_URL =
  "https://images.unsplash.com/photo-1672750771479-5ea73e9439ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGJsdWUlMjB0ZWNoJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODMyMDM3MzZ8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(username.trim(), password);
      navigate("/", { replace: true });
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Erro ao entrar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left: image */}
      <div
        className="hidden lg:flex flex-1 relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(30,58,138,0.85), rgba(37,99,235,0.65)), url(${BG_URL})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-white/95 backdrop-blur-md border border-white/25 flex items-center justify-center px-4 py-2">
              <img
                src="/logo-sm.png"
                alt="Phone Lab"
                className="h-8 w-auto object-contain"
              />
            </div>
          </div>

          <div>
            <h1 className="font-heading text-4xl xl:text-5xl font-extrabold leading-tight max-w-md">
              Sistema de gestão para sua assistência técnica.
            </h1>
            <p className="mt-4 text-white/80 text-lg max-w-md leading-relaxed">
              Controle ordens de serviço, clientes, vendas de peças e financeiro — tudo em um só lugar.
            </p>
          </div>

          <div className="text-white/60 text-sm">© {new Date().getFullYear()} Phone Lab · Assistência Técnica</div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/logo-sm.png" alt="Phone Lab" className="h-10 w-auto object-contain" />
          </div>

          <h2 className="font-heading text-3xl font-bold text-slate-900">Bem-vindo de volta</h2>
          <p className="text-slate-500 mt-2">Faça login para acessar o painel da loja.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Usuário</label>
              <input
                data-testid="login-username-input"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                className="w-full h-11 rounded-lg border border-slate-300 bg-white px-3.5 text-sm focus:border-blue-500 focus:outline-none transition-all"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Senha</label>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full h-11 rounded-lg border border-slate-300 bg-white px-3.5 pr-10 text-sm focus:border-blue-500 focus:outline-none transition-all"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                  data-testid="login-toggle-password"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {err && (
              <div
                data-testid="login-error"
                className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold text-sm inline-flex items-center justify-center gap-2 transition-all hover:-translate-y-[1px] hover:shadow-lg disabled:opacity-60 disabled:hover:translate-y-0"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Entrar
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
